/**
 * Time spent, in whole minutes (Arthur, 23 October, ruling 10): ONE parser for task completions, actions and visits.
 * api/src/duration.ts is the source; web/src/duration.ts is a byte-for-byte copy, and a test in each suite refuses
 * any difference (shared-copies.test.ts). Pure: no imports, no Node or browser API.
 *
 * Accepts "90", "90m", "2h", "0.5h", "1h30m", "1.5h", "1 h 30 min", "45 mins", "2 hours": a number of minutes, or of
 * hours, or hours then minutes. Capitals and spaces are ignored. The result must be a whole number of minutes from 1
 * to 1440 (0.25h is 15; 0.01h is refused, not rounded).
 */
export const MAX_MINUTES = 1440;

/**
 * A refusal is a CODE from this closed set, never a sentence built here (A-#135, 26 October 2026): the API sends
 * DURATION_WORDS[code], so nothing the person typed can reach a refusal, and the compiler refuses any other string.
 */
export type DurationError = 'empty' | 'unread' | 'not_whole' | 'too_small' | 'too_big';
export type Parsed = { minutes: number } | { error: DurationError };

/** The fixed sentence for each refusal: the words the screen and the API both show (ruling 10). */
export const DURATION_WORDS: Readonly<Record<DurationError, string>> = {
  empty: 'Say how long it took.',
  unread: 'That time could not be read: try 90, 1h30m or 1.5h.',
  not_whole: 'Time spent is counted in whole minutes.',
  too_small: 'Time spent must be at least one minute.',
  too_big: 'Time spent is at most 24 hours (1,440 minutes).',
};

export function parseDuration(text: string): Parsed {
  const t = String(text ?? '').toLowerCase().replace(/\s+/g, '');
  if (t === '') return { error: 'empty' };
  // hours (optional) then minutes (optional); a bare number is minutes
  const m = /^(?:(\d+(?:\.\d+)?)(?:h|hr|hrs|hour|hours))?(?:(\d+(?:\.\d+)?)(?:m|min|mins|minute|minutes)?)?$/.exec(t);
  if (!m || (m[1] === undefined && m[2] === undefined)) return { error: 'unread' };
  const hours = m[1] === undefined ? 0 : Number(m[1]);
  const mins = m[2] === undefined ? 0 : Number(m[2]);
  // "1h30" is fine, "1h30.5" is not: minutes after hours must be whole
  if (m[1] !== undefined && m[2] !== undefined && !Number.isInteger(mins)) return { error: 'unread' };
  const total = hours * 60 + mins;
  // a whole number of minutes, allowing for binary fractions (0.1h is 6.000000000000001)
  const whole = Math.round(total);
  if (Math.abs(total - whole) > 1e-9) return { error: 'not_whole' };
  if (whole < 1) return { error: 'too_small' };
  if (whole > MAX_MINUTES) return { error: 'too_big' };
  return { minutes: whole };
}

/** What a number of minutes reads as: "45 min", "2 h", "1 h 30 min". */
export function describeMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** The screen's quick picks, in minutes (ruling 10). */
export const QUICK_MINUTES: readonly number[] = [5, 15, 30, 60, 120];
